import React from "react";
import styled from "styled-components";
import Button from "../../atoms/Button";
import Tag from "../../atoms/Tag";

// 선택지 상수
export const FORMULATION_OPTIONS = ["정제", "경질캡슐", "연질캡슐", "기타"];
export const SHAPE_OPTIONS = ["원형", "타원형", "장방형", "반원형", "삼각형", "사각형", "마름모형", "오각형", "육각형", "기타"];
export const COLOR_OPTIONS = ["하양", "노랑", "주황", "분홍", "빨강", "갈색", "연두", "초록", "청록", "파랑", "남색", "자주", "보라", "회색", "투명"];
export const LINE_OPTIONS = ["없음", "+", "-", "기타"];

// 조건 타입
export interface DrugSearchConditionState {
  identifyText1: string;
  identifyText2: string;
  ignoreText1: boolean;
  ignoreText2: boolean;
  formulation: string[];
  shape: string[];
  color: string[];
  line: string[];
  limit: number;
}

// 유틸리티 함수들
export const toInputValue = (value?: string | string[]): string => {
  if (!value) return '';
  return Array.isArray(value) ? value.join(',') : value;
};

export const parseIdentifyTextInput = (input: string): string | string[] => {
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (trimmed.includes(',')) {
    const arr = trimmed.split(',').map(s => s.trim()).filter(Boolean);
    return arr.length > 0 ? arr : '';
  }
  return trimmed;
};

export const formatIdentifyText = (value?: string | string[]): string => {
  if (!value) return '-';
  return Array.isArray(value) ? value.join(', ') : value;
};

interface DrugSearchConditionFormProps {
  condition: DrugSearchConditionState;
  onChange: (condition: DrugSearchConditionState) => void;
  onSearch: () => void;
  onCancel?: () => void;
  searching: boolean;
  alwaysEditMode?: boolean;
  showCancelButton?: boolean;
  isEditMode?: boolean;
  onEnterEditMode?: () => void;
  readOnlyCondition?: {
    identifyText1?: string | string[];
    identifyText2?: string | string[];
    formulation?: string[];
    shape?: string[];
    color?: string[];
    line?: string[];
  };
  historyTag?: {
    type: 'ai' | 'user' | 'none';
  };
}

// 스타일
const Section = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SectionTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConditionItem = styled.div`
  margin-bottom: 12px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConditionLabel = styled.span`
  color: #6b7280;
  margin-right: 8px;
  display: inline-block;
  min-width: 80px;
`;

const ConditionValue = styled.span`
  color: #374151;
  font-weight: 500;
`;

const EditFormGroup = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EditFormLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TextInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TextInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4A90E2;
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const CheckboxItem = styled.label<{ checked?: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid ${props => props.checked ? '#4A90E2' : '#d1d5db'};
  background: ${props => props.checked ? '#EBF5FF' : 'white'};
  color: ${props => props.checked ? '#4A90E2' : '#374151'};

  &:hover {
    border-color: #4A90E2;
  }

  input {
    display: none;
  }
`;

const IgnoreCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;

  input {
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const DrugSearchConditionForm: React.FC<DrugSearchConditionFormProps> = ({
  condition,
  onChange,
  onSearch,
  onCancel,
  searching,
  alwaysEditMode = false,
  showCancelButton = false,
  isEditMode = false,
  onEnterEditMode,
  readOnlyCondition,
  historyTag
}) => {
  const inEditMode = alwaysEditMode || isEditMode;

  const toggleOption = (field: 'formulation' | 'shape' | 'color' | 'line', value: string) => {
    const current = condition[field];
    const newValues = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onChange({ ...condition, [field]: newValues });
  };

  const renderHistoryTag = () => {
    if (!historyTag || historyTag.type === 'none') return null;
    if (historyTag.type === 'ai') {
      return <Tag variant="Tinted" color="Green" size="Small">AI 추출</Tag>;
    }
    if (historyTag.type === 'user') {
      return <Tag variant="Tinted" color="Orange" size="Small">사용자 수정</Tag>;
    }
    return null;
  };

  return (
    <Section>
      <SectionTitle>
        <SectionTitleLeft>
          검색 조건
          {renderHistoryTag()}
        </SectionTitleLeft>
        {!alwaysEditMode && !isEditMode && onEnterEditMode && (
          <Button
            size="Small"
            type="Text"
            variant="Outlined"
            color="Primary"
            onClick={onEnterEditMode}
          >
            수정
          </Button>
        )}
      </SectionTitle>

      {inEditMode ? (
        <>
          <EditFormGroup>
            <EditFormLabel>식별문자 1</EditFormLabel>
            <TextInputWrapper>
              <TextInput
                value={condition.identifyText1}
                onChange={(e) => onChange({ ...condition, identifyText1: e.target.value })}
                disabled={condition.ignoreText1}
                placeholder="식별문자 1"
              />
              <IgnoreCheckbox>
                <input
                  type="checkbox"
                  checked={condition.ignoreText1}
                  onChange={(e) => onChange({ ...condition, ignoreText1: e.target.checked })}
                />
                무시
              </IgnoreCheckbox>
            </TextInputWrapper>
          </EditFormGroup>

          <EditFormGroup>
            <EditFormLabel>식별문자 2</EditFormLabel>
            <TextInputWrapper>
              <TextInput
                value={condition.identifyText2}
                onChange={(e) => onChange({ ...condition, identifyText2: e.target.value })}
                disabled={condition.ignoreText2}
                placeholder="식별문자 2"
              />
              <IgnoreCheckbox>
                <input
                  type="checkbox"
                  checked={condition.ignoreText2}
                  onChange={(e) => onChange({ ...condition, ignoreText2: e.target.checked })}
                />
                무시
              </IgnoreCheckbox>
            </TextInputWrapper>
          </EditFormGroup>

          <EditFormGroup>
            <EditFormLabel>제형</EditFormLabel>
            <CheckboxGroup>
              {FORMULATION_OPTIONS.map(option => (
                <CheckboxItem
                  key={option}
                  checked={condition.formulation.includes(option)}
                >
                  <input
                    type="checkbox"
                    checked={condition.formulation.includes(option)}
                    onChange={() => toggleOption('formulation', option)}
                  />
                  {option}
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </EditFormGroup>

          <EditFormGroup>
            <EditFormLabel>모양</EditFormLabel>
            <CheckboxGroup>
              {SHAPE_OPTIONS.map(option => (
                <CheckboxItem
                  key={option}
                  checked={condition.shape.includes(option)}
                >
                  <input
                    type="checkbox"
                    checked={condition.shape.includes(option)}
                    onChange={() => toggleOption('shape', option)}
                  />
                  {option}
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </EditFormGroup>

          <EditFormGroup>
            <EditFormLabel>색상</EditFormLabel>
            <CheckboxGroup>
              {COLOR_OPTIONS.map(option => (
                <CheckboxItem
                  key={option}
                  checked={condition.color.includes(option)}
                >
                  <input
                    type="checkbox"
                    checked={condition.color.includes(option)}
                    onChange={() => toggleOption('color', option)}
                  />
                  {option}
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </EditFormGroup>

          <EditFormGroup>
            <EditFormLabel>분할선</EditFormLabel>
            <CheckboxGroup>
              {LINE_OPTIONS.map(option => (
                <CheckboxItem
                  key={option}
                  checked={condition.line.includes(option)}
                >
                  <input
                    type="checkbox"
                    checked={condition.line.includes(option)}
                    onChange={() => toggleOption('line', option)}
                  />
                  {option}
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </EditFormGroup>

          <EditFormGroup>
            <EditFormLabel>검색 결과 수</EditFormLabel>
            <TextInput
              type="number"
              value={condition.limit}
              onChange={(e) => onChange({ ...condition, limit: parseInt(e.target.value) || 10 })}
              min={1}
              max={100}
              style={{ width: '80px' }}
            />
          </EditFormGroup>

          <ButtonGroup>
            <Button
              size="Small"
              type="Text"
              variant="Contained"
              color="Primary"
              onClick={onSearch}
              disabled={searching}
            >
              {searching ? '검색 중...' : '검색'}
            </Button>
            {showCancelButton && onCancel && (
              <Button
                size="Small"
                type="Text"
                variant="Outlined"
                color="Secondary"
                onClick={onCancel}
                disabled={searching}
              >
                취소
              </Button>
            )}
          </ButtonGroup>
        </>
      ) : (
        <>
          <ConditionItem>
            <ConditionLabel>식별문자 1:</ConditionLabel>
            <ConditionValue>{formatIdentifyText(readOnlyCondition?.identifyText1)}</ConditionValue>
          </ConditionItem>
          <ConditionItem>
            <ConditionLabel>식별문자 2:</ConditionLabel>
            <ConditionValue>{formatIdentifyText(readOnlyCondition?.identifyText2)}</ConditionValue>
          </ConditionItem>
          <ConditionItem>
            <ConditionLabel>제형:</ConditionLabel>
            <ConditionValue>{readOnlyCondition?.formulation?.join(', ') || '-'}</ConditionValue>
          </ConditionItem>
          <ConditionItem>
            <ConditionLabel>모양:</ConditionLabel>
            <ConditionValue>{readOnlyCondition?.shape?.join(', ') || '-'}</ConditionValue>
          </ConditionItem>
          <ConditionItem>
            <ConditionLabel>색상:</ConditionLabel>
            <ConditionValue>{readOnlyCondition?.color?.join(', ') || '-'}</ConditionValue>
          </ConditionItem>
          <ConditionItem>
            <ConditionLabel>분할선:</ConditionLabel>
            <ConditionValue>{readOnlyCondition?.line?.join(', ') || '-'}</ConditionValue>
          </ConditionItem>
        </>
      )}
    </Section>
  );
};

export default DrugSearchConditionForm;
